import { useNavigate } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { CustomBarChart } from '@/features/charts/components/chart-types/bar-chart-components/custom-bar-chart';
import { AggregationType } from '@/features/charts/components/create-chart-type-selection';
import { getKpiValueDataKey } from '@/features/charts/util/chart-util';
import { DashboardProjectCardHeaderContent } from '@/features/dashboard/components/dashboard/dashboard-project-card-header-content';
import { BaseProjectSlideCardProps } from '@/features/dashboard/components/dashboard/dashboard-project-slot-card';
import { getKpiMetricsSummary } from '@/features/dashboard/utils/kpi-metrics';
import { BlurredContainer } from '@/features/insights/components/blurred-container';
import { useKpiTranslations } from '@/features/projects/project-details/components/kpi-card';
import { getKpiUniqueId } from '@/features/projects/shared/utils/kpi-utils';
import TendencyIndicator, {
  getTendencyFromPercentageChange,
} from '@/shared/components/tendency-indicator';
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card';
import { getKpiUnitAbbreviationTranslation } from '@/shared/hooks/use-kpi-unit-translation';
import { formatDashboardNumber, formatPercentage } from '@/shared/utils/format';

export const DashboardProjectBar = ({
  project,
  kpi,
  currentCardIndex,
  onCardIndexChange,
  isPaused,
  onTogglePause,
}: BaseProjectSlideCardProps) => {
  const { tKpiTypes } = useKpiTranslations();
  const navigate = useNavigate();

  const { currentValue: kpiValue, changePercentage } = getKpiMetricsSummary(
    kpi?.metrics,
  );
  const tendency = getTendencyFromPercentageChange(changePercentage);
  const kpiUnit = kpi?.unit;

  const handleCardClick = () => {
    if (kpi) {
      navigate(
        paths.insights.getHref({
          kpiUniqueId: getKpiUniqueId(kpi),
          projectId: project.id.toString(),
        }),
      );
    }
  };

  return (
    <BlurredContainer className="size-full" isBlurred={false}>
      <Card variant="informationAlt" className="size-full p-0">
        <CardHeader className="flex flex-row items-start justify-between p-6">
          <DashboardProjectCardHeaderContent
            kpiName={kpi?.name}
            projectName={project.name}
            currentPageIndex={currentCardIndex}
            onCardIndexChange={onCardIndexChange}
            isPaused={isPaused}
            onTogglePause={onTogglePause}
          />
        </CardHeader>
        <CardContent>
          <div className="flex h-full flex-row items-end gap-4">
            <div
              className="flex h-full min-w-0 flex-1 cursor-pointer flex-col justify-end gap-4 p-5"
              onClick={handleCardClick}
            >
              <div className="flex flex-row items-center gap-1.5">
                <TendencyIndicator tendency={tendency} size="small" />
                <span className="text-2xs font-semibold text-primary-shade-grey-500">
                  {formatPercentage(changePercentage)}%
                </span>
              </div>
              <span className="truncate text-xl font-semibold leading-none">
                {formatDashboardNumber(kpiValue)}{' '}
                {getKpiUnitAbbreviationTranslation(tKpiTypes, kpiUnit ?? null)}
              </span>
            </div>
            <div className="flex h-full shrink-0 basis-[55%] items-end">
              {kpi && (
                <CustomBarChart
                  chartSettings={{
                    hideXAxis: true,
                    hideYAxis: true,
                    hideGrid: true,
                    hidePlaceholderIfEmpty: true,
                  }}
                  chartResult={{
                    chartConfig: {
                      [kpi?.id]: {
                        label: kpi?.name ?? '',
                      },
                    },
                    chartData: [
                      {
                        kpiId: kpi?.id,
                        kpiName: kpi?.name,
                        name: kpi?.name ?? '',
                        value: kpiValue,
                        budget: kpi?.max_value ?? 0,
                        aggregationType: AggregationType.ACCUMULATED,
                        [getKpiValueDataKey(kpi?.id)]: kpiValue,
                      },
                    ],
                  }}
                  colors={[]}
                  aggregationType={AggregationType.ACCUMULATED}
                  maxValue={kpi?.max_value ?? 0}
                  unit={kpiUnit ?? null}
                  className="w-full"
                  budgetPosition="right"
                  bubbleStyle="beside"
                  onBarClick={handleCardClick}
                />
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </BlurredContainer>
  );
};
